import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import styles from './Upload.module.css';
import {PRISMAX_DATA_PIPELINE_URL} from '../../configs/config';
import {SwapMainAppContext} from '../../context/SwapMainAppContext';
import {getApiErrorMessage, isSessionExpiredError} from '../../utils/authErrors';
import {validateFilesForFormat, resolveDataFormat} from './FileFormatCheck';
import {collectDroppedFiles} from './dropFiles';
import {buildManifestPayload, uploadRawFile} from './uploadFiles';
import UploadInfoModal from './UploadInfoModal';
import appearanceIcon from '../../static_files/appearance.svg';
import fieldsIcon from '../../static_files/fields.svg';
import sortIcon from '../../static_files/sort.svg';
import searchIcon from '../../static_files/search.svg';
import addIcon from '../../static_files/add.svg';
import closeButton from '../../static_files/closeButtonHover.svg';
import uploadIcon from '../../static_files/upload.svg';
import dropdownArrow from '../../static_files/dropdown-arrow.svg';

const MAX_PREVIEW_FILES = 8;
const UPLOAD_CONCURRENCY = 5;
const MANIFEST_FILENAME = '_MANIFEST.json';
const TASK_LOADING_CARD_COUNT = 12;
const DEFAULT_UPLOAD_PAUSED_MESSAGE = 'Data uploads are temporarily paused while we upgrade the system. Please come back soon.';
const SDK_URL = 'https://github.com/PrismaXAI/sdk-vla-foundry';

function normalizeRelativePath(path) {
    const normalized = (path || '').replace(/\\/g, '/');
    const parts = normalized.split('/').filter(Boolean);
    if (parts.length <= 1) {
        return parts[0] || '';
    }
    return parts.slice(1).join('/');
}

function isIgnoredPath(relativePath) {
    return relativePath === '.DS_Store'
        || relativePath.endsWith('/.DS_Store');
}

function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return '-';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatDuration(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0) return '';
    const totalSeconds = Math.max(0, Math.round(seconds));
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
        return `${String(hours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
    }

    return `${String(remainingMinutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
}

function getTaskEnvironment(task) {
    return String(task?.environment || task?.scenario || task?.scene || '').trim();
}

function getTaskTitle(task) {
    if (task?.scenario) return task.scenario;
    if (task?.details_description) return task.details_description;
    if (task?.description) return task.description;
    return `Task ${task?.task_id ?? '-'}`;
}

function getMachineLabel(machine) {
    if (machine?.product_name) return String(machine.product_name).trim();
    if (machine?.machine_name) return String(machine.machine_name).trim();
    if (machine?.machine_id !== undefined && machine?.machine_id !== null) return String(machine.machine_id);
    return '-';
}

function getTaskDescription(task) {
    if (task?.details_description) return task.details_description;
    if (task?.description) return task.description;
    if (task?.notes) return task.notes;
    return 'No description.';
}

function getTaskPreviewVideoUrl(task) {
    return String(task?.preview_video_url || '').trim();
}

function getDefaultMachineId(machineRows) {
    const defaultMachine = (machineRows || []).find((machine) => machine?.is_default_producer);
    return defaultMachine?.machine_id || null;
}

async function runWithConcurrency(items, limit, handler, onProgress) {
    let index = 0;
    let completed = 0;

    const worker = async () => {
        while (true) {
            const currentIndex = index;
            if (currentIndex >= items.length) return;
            index += 1;
            await handler(items[currentIndex]);
            completed += 1;
            if (typeof onProgress === 'function') {
                onProgress(completed);
            }
        }
    };

    const workers = Array.from({length: Math.min(limit, items.length)}, () => worker());
    await Promise.all(workers);
}

function MachineDropdown({machines, selectedMachineId, onChange}) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const selectedMachine = machines.find(
        (m) => String(m.machine_id) === String(selectedMachineId)
    );
    const selectedLabel = selectedMachine
        ? (selectedMachine.product_name || String(selectedMachine.machine_id))
        : (machines.length === 0 ? 'No machine available' : 'Select Robot');

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    return (
        <div className={styles.machineSelectRow}>
            <span className={styles.machineSelectLabel}>Robot Name</span>
            <div
                ref={containerRef}
                className={styles.machineDropdownWrapper}
            >
                {/* Trigger */}
                <button
                    type="button"
                    className={styles.machineDropdownTrigger}
                    onClick={() => machines.length > 0 && setOpen((prev) => !prev)}
                    disabled={machines.length === 0}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                >
                    <span className={styles.machineDropdownValue}>{selectedLabel}</span>
                    <img 
                        className={`${styles.machineDropdownChevron} ${open ? styles.machineDropdownChevronOpen : ''}`}
                        src={dropdownArrow}
                    />
                </button>

                {/* Panel */}
                {open && (
                    <div className={styles.machineDropdownPanel} role="listbox">
                        <span className={styles.machineDropdownPanelLabel}>Select Robot</span>
                        <div className={styles.machineDropdownItems}>
                            {machines.map((machine) => {
                                const label = machine.product_name || String(machine.machine_id);
                                const isSelected = String(machine.machine_id) === String(selectedMachineId);
                                return (
                                    <div
                                        key={machine.machine_id}
                                        role="option"
                                        aria-selected={isSelected}
                                        className={`${styles.machineDropdownItem} ${isSelected ? styles.machineDropdownItemSelected : ''}`}
                                        onMouseDown={() => {
                                            onChange(machine.machine_id);
                                            setOpen(false);
                                        }}
                                    >
                                        {label}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function TaskCardSkeletonGrid() {
    return (
        <div className={styles.cardGrid} aria-hidden="true">
            {Array.from({length: TASK_LOADING_CARD_COUNT}, (_, index) => (
                <article key={index} className={`${styles.taskCard} ${styles.taskCardSkeleton}`}>
                    <div className={styles.taskMedia}>
                        <div className={styles.loadingShimmer}/>
                    </div>
                    <div className={styles.taskText}>
                        <div className={`${styles.skeletonLine} ${styles.skeletonLineTitle}`}/>
                        <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`}/>
                    </div>
                    <div className={styles.skeletonButton}/>
                </article>
            ))}
        </div>
    );
}



const DataUpload = ({
                        scenarioFilter = '',
                        dataTasks = [],
                        dataMachines = [],
                        isDataLoading = false,
                    }) => {
    const {gatewayToken, userRole, showNotification, handleUserSessionExpired} = useContext(SwapMainAppContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [selectedMachineId, setSelectedMachineId] = useState(null);
    const [files, setFiles] = useState([]);
    const [duplicateCount, setDuplicateCount] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedCount, setUploadedCount] = useState(0);
    const [uploadResult, setUploadResult] = useState(null);
    const [isUploadInfoModalOpen, setIsUploadInfoModalOpen] = useState(false);
    const [isOperatorGateModalOpen, setIsOperatorGateModalOpen] = useState(false);
    const [isFileModalOpen, setIsFileModalOpen] = useState(false);
    const [showUploadSession, setShowUploadSession] = useState(false);
    const [isDraggingFile, setIsDraggingFile] = useState(false);
    const [uploadControl, setUploadControl] = useState({
        paused: false,
        message: DEFAULT_UPLOAD_PAUSED_MESSAGE,
    });
    const [searchText, setSearchText] = useState('');
    const [formatFilter, setFormatFilter] = useState('all');
    const [sortBy, setSortBy] = useState('created_desc');
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [resumeSession, setResumeSession] = useState(null);
    const searchInputRef = useRef(null);
    const previewVideoRefs = useRef(new Map());
    // Keep track of the URL hash to open the UploadInfoModal by task ID e.g. /data/upload#4
    const lastHandledHashRef = useRef(null);
    const [urlHash, setUrlHash] = useState(() => (typeof window !== 'undefined' ? window.location.hash : ''));
    // ?job=true alongside the hash shows an amber SDK-upload banner. Variant reflects whether
    // the hash task ID was found ('found'), not found ('not_found'), or absent ('no_task_id').
    const [jobBannerVariant, setJobBannerVariant] = useState(null);
    const [isJobBannerDismissed, setIsJobBannerDismissed] = useState(false);
    const [previewDurations, setPreviewDurations] = useState({});

    const isLoggedIn = Boolean(gatewayToken || localStorage.getItem('gatewayToken'));
    const isOperator = (userRole || '').toLowerCase() === 'operator';
    const isUploadPaused = Boolean(uploadControl.paused);
    const uploadPausedMessage = uploadControl.message || DEFAULT_UPLOAD_PAUSED_MESSAGE;

    const tasks = dataTasks || [];
    const machines = dataMachines || [];

    const selectedTask = useMemo(() => {
        return tasks.find((task) => task.task_id === selectedTaskId) || null;
    }, [tasks, selectedTaskId]);

    const selectedMachine = useMemo(() => {
        return machines.find((machine) => String(machine?.machine_id) === String(selectedMachineId)) || null;
    }, [machines, selectedMachineId]);

    const selectedTaskLabel = useMemo(() => {
        if (!selectedTask) return `Task ${selectedTaskId ?? '-'}`;
        return getTaskTitle(selectedTask);
    }, [selectedTask, selectedTaskId]);

    const selectedMachineLabel = useMemo(() => {
        if (!selectedMachine) return `Machine ${selectedMachineId ?? '-'}`;
        return getMachineLabel(selectedMachine);
    }, [selectedMachine, selectedMachineId]);

    const resumeUploadId = resumeSession?.upload_id || null;

    const resolvedDataFormat = useMemo(() => {
        return resolveDataFormat(selectedTask?.data_format);
    }, [selectedTask]);

    const scenarioTasks = useMemo(() => {
        const fallbackScenario = getTaskEnvironment(tasks[0]);
        const normalizedScenario = String(scenarioFilter || fallbackScenario || '').trim().toLowerCase();
        if (!normalizedScenario) return tasks;
        return tasks.filter((task) => {
            const env = getTaskEnvironment(task).toLowerCase();
            return env.includes(normalizedScenario);
        });
    }, [tasks, scenarioFilter]);

    const formatOptions = useMemo(() => {
        const options = new Set();
        scenarioTasks.forEach((task) => {
            const key = String(task?.data_format || '').trim() || 'Unspecified';
            options.add(key);
        });
        return Array.from(options).sort((a, b) => a.localeCompare(b));
    }, [scenarioTasks]);

    const visibleTasks = useMemo(() => {
        const term = searchText.trim().toLowerCase();
        let rows = scenarioTasks.filter((task) => {
            const taskFormat = String(task?.data_format || '').trim() || 'Unspecified';
            if (formatFilter !== 'all' && taskFormat !== formatFilter) return false;
            if (!term) return true;
            const searchable = [
                task?.task_id,
                getTaskTitle(task),
                getTaskDescription(task),
                getTaskEnvironment(task),
                task?.issuer,
                task?.action,
                task?.notes,
                task?.objects,
                taskFormat,
            ]
                .map((item) => String(item || '').toLowerCase())
                .join(' ');
            return searchable.includes(term);
        });

        const parseCreatedAt = (task) => {
            const ts = new Date(task?.created_at || 0).getTime();
            return Number.isFinite(ts) ? ts : 0;
        };

        rows = [...rows].sort((a, b) => {
            if (sortBy === 'created_asc') return parseCreatedAt(a) - parseCreatedAt(b);
            if (sortBy === 'title_asc') return getTaskTitle(a).localeCompare(getTaskTitle(b));
            if (sortBy === 'task_id_desc') return Number(b?.task_id || 0) - Number(a?.task_id || 0);
            return parseCreatedAt(b) - parseCreatedAt(a);
        });

        return rows;
    }, [scenarioTasks, searchText, formatFilter, sortBy]);
    useEffect(() => {
        setPreviewDurations({});
        previewVideoRefs.current.clear();
    }, [scenarioFilter, searchText, formatFilter, sortBy]);

    const formatValidation = useMemo(() => {
        if (!files.length) {
            return {episodeKeys: [], errors: []};
        }
        return validateFilesForFormat(files, resolvedDataFormat);
    }, [files, resolvedDataFormat]);

    const formatErrors = formatValidation.errors || [];

    const stats = useMemo(() => {
        let totalBytes = 0;
        let mcapCount = 0;
        let videoCount = 0;
        const episodeIndexes = new Set();

        files.forEach(({relative_path, size_bytes}) => {
            totalBytes += size_bytes || 0;
            if (relative_path.endsWith('.mcap')) {
                mcapCount += 1;
                if (!relative_path.includes('/')) {
                    episodeIndexes.add(relative_path.replace(/\.mcap$/, ''));
                }
            }
            if (relative_path.endsWith('.mp4')) {
                videoCount += 1;
                let prefix = relative_path.split('/')[0];
                if (prefix.endsWith('.mcap')) {
                    prefix = prefix.replace(/\.mcap$/, '');
                }
                if (prefix) episodeIndexes.add(prefix);
            }
        });

        return {
            totalBytes,
            mcapCount,
            videoCount,
            episodeCount: episodeIndexes.size,
        };
    }, [files]);

    const handlePickedFiles = (fileList) => {
        const seen = new Set();
        const normalized = [];
        let duplicates = 0;

        fileList.forEach((pickedItem) => {
            const file = pickedItem?.file || pickedItem;
            const providedRelativePath = pickedItem?.relativePath;
            const rawPath = providedRelativePath || file.webkitRelativePath || file.name;
            const relativePath = providedRelativePath
                ? rawPath.replace(/\\/g, '/').split('/').filter(Boolean).join('/')
                : normalizeRelativePath(rawPath);
            if (!relativePath || isIgnoredPath(relativePath)) return;
            if (seen.has(relativePath)) {
                duplicates += 1;
                return;
            }
            seen.add(relativePath);
            normalized.push({
                file,
                relative_path: relativePath,
                size_bytes: file.size,
                content_type: file.type || 'application/octet-stream',
            });
        });

        setDuplicateCount(duplicates);
        setFiles(normalized);
        setUploadedCount(0);
        setUploadResult(null);
        return normalized;
    };

    const handleFileChange = (event) => {
        const pickedFiles = handlePickedFiles(Array.from(event.target.files || []));
        event.target.value = '';
        if (pickedFiles.length > 0) {
            setIsFileModalOpen(false);
            setShowUploadSession(true);
        }
    };

    const handleFileDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingFile(false);
        try {
            const droppedFiles = await collectDroppedFiles(event.dataTransfer);
            const pickedFiles = handlePickedFiles(droppedFiles);
            if (pickedFiles.length > 0) {
                setIsFileModalOpen(false);
                setShowUploadSession(true);
            }
        } catch (error) {
            console.error('Failed to read dropped files:', error);
            showNotification?.('error', 'Unable to read the dropped folder. Please try again.');
        }
    };

    const loadUploadControl = useCallback(async () => {
        try {
            const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/upload-control`);
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload.success) {
                throw new Error(payload?.msg || 'Failed to load upload control.');
            }
            setUploadControl({
                paused: Boolean(payload?.data?.paused),
                message: payload?.data?.message || DEFAULT_UPLOAD_PAUSED_MESSAGE,
            });
        } catch (error) {
            console.warn('Failed to load upload control:', error);
        }
    }, []);

    useEffect(() => {
        void loadUploadControl();
        const timer = window.setInterval(() => {
            void loadUploadControl();
        }, 60000);
        return () => window.clearInterval(timer);
    }, [loadUploadControl]);

    const startUploadFlow = (taskId) => {
        if (isUploadPaused) {
            showNotification?.('warning', uploadPausedMessage);
            return;
        }
        setResumeSession(null);
        setSelectedTaskId(taskId);
        setSelectedMachineId(getDefaultMachineId(machines));
        setFiles([]);
        setDuplicateCount(0);
        setUploadedCount(0);
        setUploadResult(null);
        setShowUploadSession(false);
        setIsUploadInfoModalOpen(true);
    };

    const startDirectUploadFlow = (taskId) => {
        if (isUploadPaused) {
            showNotification?.('warning', uploadPausedMessage);
            return;
        }
        const token = gatewayToken || localStorage.getItem('gatewayToken');
        if (!token) {
            showNotification?.('warning', 'Please log in to upload data.');
            return;
        }
        setResumeSession(null);
        setSelectedTaskId(taskId);
        setSelectedMachineId(getDefaultMachineId(machines));
        setFiles([]);
        setDuplicateCount(0);
        setUploadedCount(0);
        setUploadResult(null);
        setShowUploadSession(false);
        setIsUploadInfoModalOpen(false);

        if (!isOperator) {
            setIsOperatorGateModalOpen(true);
            return;
        }

        setIsFileModalOpen(true);
    };

    const handleUploadInfoContinue = () => {
        if (isUploadPaused) {
            setIsUploadInfoModalOpen(false);
            showNotification?.('warning', uploadPausedMessage);
            return;
        }
        const token = gatewayToken || localStorage.getItem('gatewayToken');
        if (!token) {
            showNotification?.('warning', 'Please log in to upload data.');
            return;
        }
        if (!isOperator) {
            setIsUploadInfoModalOpen(false);
            setIsOperatorGateModalOpen(true);
            return;
        }
        if (!selectedMachineId) {
            setSelectedMachineId(getDefaultMachineId(machines));
        }
        setIsUploadInfoModalOpen(false);
        setIsFileModalOpen(true);
    };

    const handleRegisterOperator = () => {
        setIsOperatorGateModalOpen(false);
        navigate('/account?open=register-robot', {
            state: {openOperatorRegisterModal: true},
        });
    };

    const handlePreviewMetadataLoaded = (taskId, event) => {
        const nextDuration = event?.currentTarget?.duration;
        if (!Number.isFinite(nextDuration) || nextDuration <= 0) return;

        setPreviewDurations((current) => {
            const taskKey = String(taskId);
            const nextFormattedDuration = formatDuration(nextDuration);
            if (current[taskKey] === nextFormattedDuration) {
                return current;
            }
            return {
                ...current,
                [taskKey]: nextFormattedDuration,
            };
        });
    };

    const canUpload = isOperator
        && !isUploadPaused
        && selectedTaskId
        && selectedMachineId
        && files.length > 0
        && !isUploading
        && formatErrors.length === 0;

    useEffect(() => {
        if (isSearchOpen) {
            searchInputRef.current?.focus();
        }
    }, [isSearchOpen]);

    useEffect(() => {
        if (!isUploadPaused || isUploading) return;
        setIsUploadInfoModalOpen(false);
        setIsFileModalOpen(false);
        setShowUploadSession(false);
    }, [isUploadPaused, isUploading]);

    useEffect(() => {
        if (!isFileModalOpen) return;
        if (selectedMachineId) return;
        const defaultMachineId = getDefaultMachineId(machines);
        if (defaultMachineId) {
            setSelectedMachineId(defaultMachineId);
        }
    }, [isFileModalOpen, selectedMachineId, machines]);

    useEffect(() => {
        const resumeUpload = location.state?.resumeUpload;
        if (!resumeUpload?.upload_id) return;

        setResumeSession({
            upload_id: resumeUpload.upload_id,
            task_id: resumeUpload.task_id,
            machine_id: resumeUpload.machine_id,
        });
        setSelectedTaskId(resumeUpload.task_id ?? null);
        setSelectedMachineId(resumeUpload.machine_id ?? getDefaultMachineId(machines));
        setFiles([]);
        setDuplicateCount(0);
        setUploadedCount(0);
        setUploadResult(null);
        setShowUploadSession(false);
        setIsUploadInfoModalOpen(false);
        setIsFileModalOpen(true);

        navigate(location.pathname, {replace: true, state: {}});
    }, [location.pathname, location.state, machines, navigate]);

    useEffect(() => {
        const handleHashChange = () => setUrlHash(window.location.hash);
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    useEffect(() => {
        if (isDataLoading || !tasks.length) return;
        const hashTaskId = urlHash.replace(/^#/, '').trim();
        if (!hashTaskId) return;
        if (lastHandledHashRef.current === hashTaskId) return;
        lastHandledHashRef.current = hashTaskId;
        const matchedTask = tasks.find((task) => String(task.task_id) === hashTaskId);
        if (matchedTask) {
            startUploadFlow(matchedTask.task_id);
        }
    }, [urlHash, isDataLoading, tasks]);

    useEffect(() => {
        const isJobUpload = new URLSearchParams(location.search).get('job') === 'true';
        if (!isJobUpload) {
            setJobBannerVariant(null);
            return;
        }

        const hashTaskId = urlHash.replace(/^#/, '').trim();
        let nextVariant;
        if (!hashTaskId) {
            nextVariant = 'no_task_id';
        } else {
            // Task lookup needs tasks to be loaded; leave the current variant until they are.
            if (isDataLoading || !tasks.length) return;
            const matchedTask = tasks.find((task) => String(task.task_id) === hashTaskId);
            nextVariant = matchedTask ? 'found' : 'not_found';
        }

        setJobBannerVariant((current) => {
            if (current !== nextVariant) {
                setIsJobBannerDismissed(false);
            }
            return nextVariant;
        });
    }, [location.search, urlHash, isDataLoading, tasks]);

    const handleToggleSearch = () => {
        if (isSearchOpen) {
            setSearchText('');
            setIsSearchOpen(false);
            return;
        }
        setIsSearchOpen(true);
    };

    const handleResetDiscovery = () => {
        setSearchText('');
        setFormatFilter('all');
        setSortBy('created_desc');
        setIsSearchOpen(false);
    };

    const handleUpload = async () => {
        if (isUploadPaused) {
            showNotification?.('warning', uploadPausedMessage);
            return;
        }
        if (!isOperator) {
            setIsOperatorGateModalOpen(true);
            return;
        }
        const token = gatewayToken || localStorage.getItem('gatewayToken');
        if (!token) {
            showNotification?.('error', 'Missing gateway token. Please log in again.');
            return;
        }
        if (!selectedTaskId || !selectedMachineId) {
            showNotification?.('error', 'Please select a task and a machine.');
            return;
        }
        if (!files.length) {
            showNotification?.('error', 'Please choose files to upload.');
            return;
        }
        if (formatErrors.length) {
            showNotification?.('error', formatErrors.join('; '));
            return;
        }
        if (!formatValidation.episodeKeys.length) {
            showNotification?.('error', 'No episode keys detected. Ensure files follow {episode_key}.mcap and {episode_key}/video.mp4.');
            return;
        }

        setIsUploading(true);
        setUploadedCount(0);
        setUploadResult(null);
        let activeUploadId = resumeUploadId;

        try {
            const manifestPlaceholders = formatValidation.episodeKeys.map((episodeKey) => ({
                relative_path: `${episodeKey}/${MANIFEST_FILENAME}`,
                size_bytes: null,
                content_type: 'application/json',
            }));

            const payload = {
                token,
                files: [
                    ...files.map(({relative_path, size_bytes, content_type}) => ({
                        relative_path,
                        size_bytes,
                        content_type,
                    })),
                    ...manifestPlaceholders,
                ],
            };
            if (!resumeUploadId) {
                payload.task_id = selectedTaskId;
                payload.machine_id = selectedMachineId;
            }

            const sessionUrl = resumeUploadId
                ? `${PRISMAX_DATA_PIPELINE_URL}/data/upload-sessions/${resumeUploadId}/resume`
                : `${PRISMAX_DATA_PIPELINE_URL}/data/upload-sessions`;

            const sessionRes = await fetch(sessionUrl, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload),
            });

            const sessionJson = await sessionRes.json();
            if (!sessionRes.ok || !sessionJson.success) {
                throw new Error(getApiErrorMessage(sessionJson, 'Failed to create upload session'));
            }

            const {
                upload_id,
                signed_urls,
                upload_prefix,
                bucket,
                existing_files = [],
                missing_files = [],
            } = sessionJson.data;
            activeUploadId = upload_id;
            setResumeSession({
                upload_id,
                task_id: sessionJson.data.task_id ?? selectedTaskId,
                machine_id: sessionJson.data.machine_id ?? selectedMachineId,
            });
            const urlMap = new Map((signed_urls || []).map(item => [item.relative_path, item.signed_url]));
            const filesToUpload = files.filter((item) => urlMap.has(item.relative_path));
            const existingRawCount = files.length - filesToUpload.length;
            setUploadedCount(existingRawCount);

            await runWithConcurrency(
                filesToUpload,
                UPLOAD_CONCURRENCY,
                (item) => uploadRawFile(item, urlMap.get(item.relative_path)),
                (count) => setUploadedCount(existingRawCount + count)
            );

            for (const episodeKey of formatValidation.episodeKeys) {
                const manifestPath = `${episodeKey}/${MANIFEST_FILENAME}`;
                const manifestUrl = urlMap.get(manifestPath);
                if (!manifestUrl) {
                    continue;
                }
                const manifestPayload = buildManifestPayload({
                    episodeKey,
                    uploadId: upload_id,
                    machineId: selectedMachineId,
                    taskId: selectedTaskId,
                    files,
                });
                const manifestBlob = new Blob(
                    [JSON.stringify(manifestPayload, null, 2)],
                    {type: 'application/json'}
                );
                const response = await fetch(manifestUrl, {
                    method: 'PUT',
                    body: manifestBlob,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                if (!response.ok) {
                    throw new Error(`Manifest upload failed for ${episodeKey}`);
                }
            }

            setUploadResult({
                upload_id,
                bucket,
                upload_prefix,
                total_files: files.length,
                existing_files: existing_files.length,
                missing_files: missing_files.length,
                resumed: Boolean(resumeUploadId),
            });
            setResumeSession(null);
            showNotification?.('success', resumeUploadId
                ? 'Upload resumed. Worker will process episodes.'
                : 'Upload completed. Worker will process episodes.');
        } catch (error) {
            console.error('Upload error:', error);
            if (isSessionExpiredError(error)) {
                handleUserSessionExpired?.();
                return;
            }
            const message = error.message || 'Upload failed';
            showNotification?.(
                'error',
                activeUploadId
                    ? `${message} Upload #${activeUploadId} can be continued.`
                    : message
            );
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <>
            {jobBannerVariant && !isJobBannerDismissed && (
                <div className={styles.jobBanner} role="status">
                    <span>
                        Please use the SDK to upload your data under the job assigned to you{' '}
                        <a href={SDK_URL} target="_blank" rel="noopener noreferrer" className={styles.jobBannerLink}>
                            (open SDK <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" className={styles.jobBannerLinkIcon}>
                                <path d="M2.5 7.5L7.5 2.5M7.5 2.5H3.5M7.5 2.5V6.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>)
                        </a>
                        {jobBannerVariant === 'not_found'
                            ? ". This task isn't available to view on our web platform."
                            : '.'}
                    </span>
                    <button
                        type="button"
                        className={styles.jobBannerDismiss}
                        onClick={() => setIsJobBannerDismissed(true)}
                        aria-label="Dismiss"
                    >
                        ×
                    </button>
                </div>
            )}
            <div className={styles.page}>
            <div className={styles.toolbar}>
                <span className={styles.toolItem}><img src={appearanceIcon} alt="" className={styles.toolIcon}/>Appearance</span>
                <span className={styles.toolItem}><img src={fieldsIcon} alt=""
                                                       className={styles.toolIcon}/>Fields</span>
                <span className={styles.toolItem}>1 Visible</span>
                <span className={styles.toolItem}><img src={sortIcon} alt="" className={styles.toolIcon}/>Sort</span>
                <span className={styles.toolActions}>
                    <button type="button" className={styles.actionBtn} onClick={handleToggleSearch}>
                        <img src={searchIcon} alt="" className={styles.actionIcon}/>
                    </button>
                    <button type="button" className={styles.actionBtn} onClick={handleResetDiscovery}>
                        <img src={addIcon} alt="" className={styles.actionIcon}/>
                    </button>
                </span>
            </div>

            {isSearchOpen && (
                <div className={styles.queryRow}>
                    <input
                        ref={searchInputRef}
                        className={styles.searchInput}
                        type="text"
                        placeholder="Search by task id, title, notes..."
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                    />
                    <select
                        className={styles.filterSelect}
                        value={formatFilter}
                        onChange={(event) => setFormatFilter(event.target.value)}
                    >
                        <option value="all">All Formats</option>
                        {formatOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                    <select
                        className={styles.sortSelect}
                        value={sortBy}
                        onChange={(event) => setSortBy(event.target.value)}
                    >
                        <option value="created_desc">Newest</option>
                        <option value="created_asc">Oldest</option>
                        <option value="title_asc">Title A-Z</option>
                        <option value="task_id_desc">Task ID Desc</option>
                    </select>
                </div>
            )}

            {isUploadPaused ? (
                <div className={styles.uploadPausedState}>
                    <div className={styles.uploadPausedBadge}>Upload paused</div>
                    <h2>System upgrade in progress</h2>
                    <p>{uploadPausedMessage}</p>
                    <button type="button" onClick={loadUploadControl}>
                        Check again
                    </button>
                </div>
            ) : isDataLoading ? (
                <TaskCardSkeletonGrid />
            ) : visibleTasks.length === 0 ? (
                <div className={styles.emptyState}>
                    {isLoggedIn ? 'No tasks in this scenario.' : 'Please log in to view upload tasks.'}
                </div>
            ) : (
                <div className={styles.cardGrid}>
                    {visibleTasks.map((task) => {
                        const taskKey = String(task.task_id);
                        const taskPreviewUrl = getTaskPreviewVideoUrl(task);

                        return (
                        <article
                            key={task.task_id}
                            className={styles.taskCard}
                            role="button"
                            tabIndex={0}
                            onClick={() => startUploadFlow(task.task_id)}
                            onKeyDown={(event) => {
                                if (event.target !== event.currentTarget) return;
                                if (event.key !== 'Enter' && event.key !== ' ') return;
                                event.preventDefault();
                                startUploadFlow(task.task_id);
                            }}
                        >
                            <div className={styles.taskMedia}>
                                {taskPreviewUrl ? (
                                    <video
                                        ref={(element) => {
                                            if (!element) {
                                                previewVideoRefs.current.delete(taskKey);
                                                return;
                                            }
                                            previewVideoRefs.current.set(taskKey, element);
                                        }}
                                        className={styles.taskMediaVideo}
                                        src={taskPreviewUrl}
                                        muted
                                        autoPlay
                                        loop
                                        playsInline
                                        preload="auto"
                                        onLoadedMetadata={(event) => handlePreviewMetadataLoaded(task.task_id, event)}
                                    />
                                ) : null}
                                {taskPreviewUrl && previewDurations[taskKey] ? (
                                    <span className={styles.taskMediaTime}>{previewDurations[taskKey]}</span>
                                ) : null}
                            </div>
                            <div className={styles.taskText}>
                                <div className={styles.taskTitle}>{getTaskTitle(task)}</div>
                            </div>
                            <button
                                type="button"
                                className={styles.uploadButton}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    startDirectUploadFlow(task.task_id);
                                }}
                            >
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                                    <path
                                        d="M6.5 9V2M3.5 5L6.5 2L9.5 5"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M2.5 9.5V10.5C2.5 11.0523 2.94772 11.5 3.5 11.5H9.5C10.0523 11.5 10.5 11.0523 10.5 10.5V9.5"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                    />
                                </svg>
                                Upload
                            </button>
                        </article>
                    );
                    })}
                </div>
            )}

            {showUploadSession && (
                <div className={styles.modalBackdrop}>
                    <div className={`${styles.modalLarge} ${styles.uploadSessionModal}`}>
                        <div className={styles.uploadSessionTop}>
                            <div className={styles.uploadHeader}>
                                <div>
                                    <h2>Upload Session</h2>
                                    <p>
                                        {selectedTaskLabel} / {selectedMachineLabel}
                                        {resumeUploadId ? ` / Upload #${resumeUploadId}` : ''}
                                    </p>
                                </div>
                                <div className={styles.metaBox}>
                                    <div><span>Episodes</span><strong>{stats.episodeCount}</strong></div>
                                    <div><span>MCAP</span><strong>{stats.mcapCount}</strong></div>
                                    <div><span>Videos</span><strong>{stats.videoCount}</strong></div>
                                    <div><span>Total</span><strong>{formatBytes(stats.totalBytes)}</strong></div>
                                </div>
                            </div>
                            <img
                                src={closeButton}
                                className={styles.closeBtn}
                                onClick={() => setShowUploadSession(false)}
                                alt="Close"
                            />
                        </div>

                        <div className={styles.uploadBody}>
                            <div className={styles.selectionInfo}>
                                <span>Task: {selectedTaskLabel}</span>
                                <span>Machine: {selectedMachineLabel}</span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowUploadSession(false);
                                        setIsFileModalOpen(true);
                                    }}
                                    className={styles.linkButton}
                                    disabled={isUploading}
                                >
                                    Replace Files
                                </button>
                            </div>

                            {duplicateCount > 0 && (
                                <div className={styles.warning}>Skipped {duplicateCount} duplicate paths.</div>
                            )}
                            {formatErrors.length > 0 && (
                                <div className={styles.warning}>{formatErrors.join('; ')}</div>
                            )}

                            <div className={styles.preview}>
                                {files.map((item) => (
                                    <div key={item.relative_path} className={styles.previewItem}>
                                        <span>{item.relative_path}</span>
                                        <span>{formatBytes(item.size_bytes)}</span>
                                    </div>
                                ))}
                            </div>

                            {!uploadResult && (
                                <div className={styles.actions}>
                                <button
                                    className={canUpload ? styles.primaryButton : styles.disabledButton}
                                    disabled={!canUpload}
                                    onClick={handleUpload}
                                >
                                    {isUploading ? 'Uploading' : (resumeUploadId ? 'Continue Upload' : 'Start Upload')}
                                </button>
                                </div>
                            )}

                            {(isUploading || uploadedCount > 0) && (
                                <div className={styles.progressBar}>
                                    <div
                                        className={styles.progressFill}
                                        style={{width: `${files.length ? (uploadedCount / files.length) * 100 : 0}%`}}
                                    />
                                    <span>{uploadedCount}/{files.length} uploaded</span>
                                </div>
                            )}

                            {uploadResult && (
                                <div className={styles.successBox}>
                                    {uploadResult?.resumed ? 'Successfully resumed upload.' : 'Successfully uploaded.'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isUploadInfoModalOpen && selectedTask && (
                 <UploadInfoModal
                    task={selectedTask}
                    onClose={() => setIsUploadInfoModalOpen(false)}
                    onUpload={handleUploadInfoContinue}
                />
            )}

            {isOperatorGateModalOpen && (
                <div
                    className={styles.operatorGateBackdrop}
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            setIsOperatorGateModalOpen(false);
                        }
                    }}
                >
                    <div className={styles.operatorGateModal} role="dialog" aria-modal="true" aria-labelledby="operator-gate-title">
                        <button
                            type="button"
                            className={styles.operatorGateClose}
                            onClick={() => setIsOperatorGateModalOpen(false)}
                            aria-label="Close"
                        >
                            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                                <path d="M2 2L13 13M13 2L2 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </button>

                        <div className={styles.operatorGateIcon}>
                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                                <rect x="4" y="7.5" width="14" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.4" />
                                <circle cx="8.3" cy="12.5" r="1.2" fill="currentColor" />
                                <circle cx="13.7" cy="12.5" r="1.2" fill="currentColor" />
                                <path d="M11 7.5V4" stroke="currentColor" strokeWidth="1.4" />
                                <circle cx="11" cy="3" r="1.3" fill="currentColor" />
                            </svg>
                        </div>

                        <h2 id="operator-gate-title" className={styles.operatorGateTitle}>Register as an operator to upload</h2>
                        <p className={styles.operatorGateText}>
                            Uploading demonstrations is reserved for registered operators. To qualify, you'll need a robot arm to capture episodes &mdash; you're welcome to use your own.
                        </p>

                        <button
                            type="button"
                            className={styles.operatorGateFleetCard}
                            onClick={() => {
                                setIsOperatorGateModalOpen(false);
                                navigate('/data/fleet');
                            }}
                        >
                            <span className={styles.operatorGateFleetCopy}>
                                <span className={styles.operatorGateOptional}>Optional</span>
                                <strong>Don't have a robot arm yet?</strong>
                                <span>Browse our fleet to find one and get capturing.</span>
                            </span>
                            <span className={styles.operatorGateArmIcon} aria-hidden="true">
                                🦾
                            </span>
                            <svg className={styles.operatorGateChevron} width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        <div className={styles.operatorGateActions}>
                            <button type="button" className={styles.operatorGatePrimary} onClick={handleRegisterOperator}>
                                Register as operator
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                                    <path d="M3 6.5H10M7 3.5L10 6.5L7 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            <button type="button" className={styles.operatorGateSecondary} onClick={() => setIsOperatorGateModalOpen(false)}>
                                Maybe later
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isFileModalOpen && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.modalLarge}>
                        <div className={styles.modalHeader}>
                            <img src={closeButton} className={styles.closeBtn} onClick={() => setIsFileModalOpen(false)}
                                 alt="Close"/>
                        </div>


                        <div
                            className={isDraggingFile ? `${styles.dropArea} ${styles.dropAreaActive}` : styles.dropArea}
                            onDragOver={(event) => {
                                event.preventDefault();
                                setIsDraggingFile(true);
                            }}
                            onDragLeave={(event) => {
                                event.preventDefault();
                                setIsDraggingFile(false);
                            }}
                            onDrop={handleFileDrop}
                        >
                            <MachineDropdown
                                machines={machines}
                                selectedMachineId={selectedMachineId}
                                onChange={(id) => setSelectedMachineId(id || null)}
                            />
                            <img src={uploadIcon} alt="" className={styles.uploadIcon}/>
                            <p>Drag and drop to upload Mcap and video files</p>
                            <p className={styles.dropHint}>Supported file types include mp4, mcap.</p>
                            <label className={styles.filePicker}>
                                <input
                                    type="file"
                                    webkitdirectory=""
                                    directory=""
                                    multiple
                                    onChange={handleFileChange}
                                />
                                <span>Upload</span>
                            </label>
                            <span className={styles.dropCount}>
                                {files.length ? `${files.length} files selected` : 'No files selected'}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </>
    );
};

export default DataUpload;
